import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const TYPE_CONFIG = {
  contractor:     { icon: '👤', label: 'Contractor',       route: (id) => `/contractors/${id}` },
  vendor:         { icon: '🏢', label: 'Vendor',           route: (id) => `/vendors/${id}` },
  client:         { icon: '🏦', label: 'Client',           route: (id) => `/clients/${id}` },
  work_order:     { icon: '📋', label: 'Work Order',       route: (id) => `/work-orders/${id}` },
  purchase_order: { icon: '📦', label: 'Purchase Order',   route: (id) => `/purchase-orders/${id}` },
  ar_invoice:     { icon: '📥', label: 'AR Invoice',       route: (id) => `/ar-invoices/${id}` },
  ap_invoice:     { icon: '📤', label: 'AP Invoice',       route: (id) => `/ap-invoices/${id}` },
  batch_ref:      { icon: '🏛️', label: 'Hexaware Batch',  route: (id) => `/ar-invoices?search=${id}` },
};

const PLACEHOLDERS = [
  'Search contractor, vendor, client…',
  'Search AR/AP invoice number…',
  'Search PO number (CWK, A-prefix)…',
  'Search Hexaware batch ref…',
  'Search work order number…',
  'Search vendor or client name…',
];

export default function UniversalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [placeholder, setPlaceholder] = useState(PLACEHOLDERS[0]);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Cycle placeholder text
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIdx(i => {
        const next = (i + 1) % PLACEHOLDERS.length;
        setPlaceholder(PLACEHOLDERS[next]);
        return next;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Keyboard shortcut Ctrl+K / Cmd+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
        setResults(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults(null); return; }
    setLoading(true);
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(q)}`);
      setResults(res.data);
    } catch { setResults(null); }
    finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 250);
  };

  const handleSelect = (type, id) => {
    const config = TYPE_CONFIG[type];
    if (config) navigate(config.route(id));
    setOpen(false);
    setQuery('');
    setResults(null);
  };

  const totalResults = results?.groups?.reduce((sum, g) => sum + g.results.length, 0) || 0;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', maxWidth: 520 }}>
      {/* Search Input */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <span style={{ position: 'absolute', left: 10, color: 'var(--muted)', fontSize: 14, pointerEvents: 'none' }}>🔍</span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          style={{
            width: '100%',
            paddingLeft: 32,
            paddingRight: 60,
            height: 36,
            borderRadius: 'var(--radius)',
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontSize: 13,
            outline: 'none',
            transition: 'border-color 0.15s',
          }}
        />
        <span style={{
          position: 'absolute', right: 8,
          fontSize: 10, color: 'var(--muted)',
          background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: 4, padding: '1px 5px', fontFamily: 'monospace'
        }}>⌘K</span>
      </div>

      {/* Dropdown */}
      {open && (query.length >= 2) && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          zIndex: 9999, maxHeight: 480, overflowY: 'auto'
        }}>
          {loading && (
            <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--muted)' }}>Searching…</div>
          )}

          {!loading && results && totalResults === 0 && (
            <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--muted)' }}>
              No results for "<strong>{query}</strong>"
              <div style={{ marginTop: 8, fontSize: 11 }}>
                Try: contractor name, invoice #, PO number, vendor, batch ref
              </div>
            </div>
          )}

          {!loading && results?.groups?.map(group => (
            <div key={group.type}>
              {/* Group Header */}
              <div style={{
                padding: '6px 16px 4px',
                fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                color: 'var(--muted)', textTransform: 'uppercase',
                borderTop: '1px solid var(--border)',
                background: 'var(--bg)'
              }}>
                {TYPE_CONFIG[group.type]?.icon} {group.label}
              </div>

              {/* Results */}
              {group.results.map(result => (
                <button
                  key={result.id}
                  onClick={() => handleSelect(group.type, result.id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', padding: '8px 16px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    textAlign: 'left', gap: 8,
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-subtle, rgba(99,102,241,0.08))'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', 
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {result.label}
                    </div>
                    {result.sublabel && (
                      <div style={{ fontSize: 11, color: 'var(--muted)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {result.sublabel}
                      </div>
                    )}
                  </div>
                  <span style={{
                    fontSize: 10, color: 'var(--muted)', flexShrink: 0,
                    background: 'var(--bg)', border: '1px solid var(--border)',
                    borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap'
                  }}>
                    {TYPE_CONFIG[group.type]?.label}
                  </span>
                </button>
              ))}
            </div>
          ))}

          {/* Footer hint */}
          {!loading && totalResults > 0 && (
            <div style={{
              padding: '8px 16px', fontSize: 11, color: 'var(--muted)',
              borderTop: '1px solid var(--border)', background: 'var(--bg)',
              display: 'flex', gap: 16
            }}>
              <span>↵ to navigate</span>
              <span>esc to close</span>
              <span style={{ marginLeft: 'auto' }}>{totalResults} result{totalResults !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      )}

      {/* Search hint when empty and focused */}
      {open && query.length < 2 && !loading && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          zIndex: 9999, padding: '12px 16px'
        }}>
          <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, marginBottom: 8 }}>
            SEARCH ACROSS
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
            {Object.entries(TYPE_CONFIG).map(([type, config]) => (
              <div key={type} style={{ fontSize: 12, color: 'var(--muted)', padding: '2px 0' }}>
                {config.icon} {config.label}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--muted)', borderTop: '1px solid var(--border)', paddingTop: 8 }}>
            Type at least 2 characters to search
          </div>
        </div>
      )}
    </div>
  );
}
